import { AsyncPipe, isPlatformBrowser, Location } from "@angular/common";
import { Component, inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from "@angular/core";
import { Firestore, doc, getDoc } from "@angular/fire/firestore";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Data, NavigationEnd, NavigationStart, Router } from "@angular/router";
import { Device } from "@capacitor/device";
import { AuthService } from "@core/auth.service";
import { UserVersionService } from "@core/AuthServices/authUserVersion/auth-user-version.service";
import { LoadingService } from "@core/loading.service";
import { UtilityService } from "@core/utility.service";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { IonicModule, Platform } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subject, firstValueFrom, interval } from "rxjs";
import { filter, startWith, take, takeUntil, timeout } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CommonVariable, Orientation } from "./app.constants";
import {
  ADD_HOME_SCREEN_DOC_ID,
  resolveOperatingSystem,
  upsertInstalledDevice,
} from "./components/add-home-screen-banner/add-home-screen.helpers";
import { StuckWebsiteComponent } from "./app/stuck-website/stuck-website.component";
import { AnimationService } from "./services/animation.service";
import { AppInitializerService } from "./services/AppRootService/app-initializer.service";
import { AppPaymentService } from "./services/AppRootService/app-payment.service";
import { AppRouteService } from "./services/AppRootService/app-route.service";
import { NarratorTipService } from "./services/AppRootService/narrator-tip.service";
import { AppVersionService } from "./services/appVersions/app-version.service";
import { CampaignTrackingService } from './services/campaign-tracking.service';
import { GTMSegmentService } from "./services/gtm-segment.service";
import { HealthCheckService } from "./services/health-check.service";
import { HomeDataService } from "./services/home-data.service";
import { InteractionService } from "./services/interaction.service";
import { MarketingCampaignsService } from "./services/marketing-campaigns.service";
import { OneSignalService } from "./services/one-signal.service";
import { OrientationService } from "./services/orientation.service";
import { PerformanceLaunchMonitorService } from "./services/performance-launch-monitor.service";
import { PerformanceService } from "./services/performance.service";
import { PwaLaunchTrackingService } from "./services/pwa-launch-tracking.service";
import { SentryContextService } from "./services/sentry-context.service";
import { SwUpdateService } from "./services/sw-update.service";
import { ClarityService } from "./services/clarity.service";
import { PosthogService } from "./services/posthog.service";
// const { ScreenOrientation } = Plugins;
@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: true,
  imports: [IonicModule, AsyncPipe, TranslatePipe],
})
export class AppComponent implements OnInit, OnDestroy {
  userAuthDetails$ = this.authService.userDetails$;
  isLandscapeMode = false;
  holdingMessage$ = this.authService?.holdingMessage$;
  // defaultHomeCard$ = this.homeDataService?.defaultHomeCard$;
  translatedToastMsg: any;
  selectedLanguage: string = CommonVariable?.preferredLanguage;
  sqlAPICallResponse: string;
  offers: Data;
  proceed = true;
  sub: Data;
  progressBar: number;
  spinnerAnimation: boolean;
  public languageCode: string;
  public preferredCurrency: string;
  public checkOutDetails: string;
  public isStuck = false;
  private checkingStuck = false;
  private destroyed = false;
  // Becomes true once the app has successfully rendered its first route. A rendered
  // route is concrete proof the app is alive, so the launch-stuck detector must not
  // flag a merely-slow load as "stuck" after this point.
  private hasRenderedRoute = false;
  public threshold = 10000; // 10 seconds
  /** Dedupes NavigationStart payment= deep-link handling for the same offer. */
  private lastPaymentDeepLinkKey: string | null = null;
  private unsubscribe$ = new Subject<void>();
  public userId: string;
  private auth = inject(AuthService);
  private firestore = inject(Firestore);
  private swUpdate = inject(SwUpdateService);
  private clarityService = inject(ClarityService);
  private posthogService = inject(PosthogService);
  private platformId = inject(PLATFORM_ID);
  constructor(
    private authService: AuthService,
    private appVersion: AppVersionService,
    public translate: TranslateService,
    private router: Router,
    private GTMSagment: GTMSegmentService,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
    private matDialog: MatDialog,
    private renderer: Renderer2,
    private platform: Platform,
    private _location: Location,
    private orientationService: OrientationService,
    private oneSignalService: OneSignalService,
    private interactionService: InteractionService,
    private healthCheckService: HealthCheckService,
    private performanceService: PerformanceService,
    private sentryContextService: SentryContextService,
    private userVersionService: UserVersionService,
    private animationService: AnimationService,
    private homeDataService: HomeDataService,
    private marketingCampaignsService: MarketingCampaignsService,
    private narratortipService: NarratorTipService,
    private appPaymentService: AppPaymentService,
    private appInitializerService: AppInitializerService,
    private appRouteService: AppRouteService,
    private performanceLaunchMonitor: PerformanceLaunchMonitorService,
    private campaignTrackingService: CampaignTrackingService,
    private pwaLaunchTrackingService: PwaLaunchTrackingService
  ) {
    this.progressBar = 5;
    this.languageCode = CommonVariable?.preferredLanguage;
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.authService?.selectedlanguage$?.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (l) => {
        this.selectedLanguage = l;
        translate.use(l ? l : CommonVariable?.preferredLanguage);
      },
      (err) => {
        return err;
      }
    );
    this.userAuthDetails$?.pipe(takeUntil(this.unsubscribe$)).subscribe((user: Data) => {
      if (user?.redirect === "clevertap") {
        const intentPreview = document.getElementById("intentPreview");
        const intentOpacityDiv = document.getElementById("intentOpacityDiv");
        if (intentPreview && intentOpacityDiv) {
          this.renderer.addClass(intentPreview, "hidePopup");
          this.renderer.addClass(intentOpacityDiv, "hidePopup");
        }
        this.authService.resetUserRedirect("");
      }
    });
    this.getDeviceInfo()
      .then(() => {
        const initCondition =
          !UtilityService?.isAppInstalledOnHomePage() && CommonVariable?.device?.operatingSystem === "ios";
        const currentLocation = location.href;
        const isSalesRoute = CommonVariable?.routesToPreventPopState?.find((route) => currentLocation.includes(route));
        if (!initCondition && !isSalesRoute) {
          this.oneSignalService.init();
        }
        void this.markAddHomeScreenInstalledIfNeeded();
      })
      .catch((err) => {
        return err;
      });
    this.GTMSagment.GTMInitilization();
    // Reuse stored device fingerprint when present — do not recompute/overwrite on every start
    const storedFingerprint = localStorage.getItem("fingerSalt");
    if (storedFingerprint) {
      CommonVariable.FingerprintJSConst = storedFingerprint;
      setTimeout(() => {
        this.appVersion.checkDeviceAppVersion(storedFingerprint);
      }, 120000);
      this.appVersion.checkDeviceAppVersion(storedFingerprint);
    } else {
      this.fingerprintJS()
        .then((data: any) => {
          if (!data || typeof data !== "string") {
            return data;
          }
          CommonVariable.FingerprintJSConst = data;
          localStorage.setItem("fingerSalt", data);
          setTimeout(() => {
            this.appVersion.checkDeviceAppVersion(data);
          }, 120000);
          this.appVersion.checkDeviceAppVersion(CommonVariable.FingerprintJSConst);
        })
        .catch((error) => {
          return error;
        });
    }

    // Clarity service initialization to track user journey and events (consent is applied inside init after load)
    this.clarityService.init(environment?.clarity?.projectId || "wcne4x6rr4");
    this.clarityService.bindRouteTracking(
      this.router,
      () => this.userId,
      () => this.selectedLanguage || "en-GB",
      () => this.preferredCurrency || "GBP",
      () => this.authService.pwaSegmentStatus || "null"
    );
    // HATDEV-100772: PostHog DEV spike — inert when environment.posthog.apiKey is empty
    this.posthogService.init();

    this.HotJarIdentify();

    // check if back button is pressed and orientation is locked to landscape then lock the orientation to portrait
    this.platform.backButton.subscribeWithPriority(10, (processNextHandler) => {
      console.info("Back press handler!");
      setTimeout(() => {
        if (this.getOrientation() === "LANDSCAPE" && CommonVariable.device?.operatingSystem == "android") {
          this.orientationService.enterFullscreen();
          this.orientationService.lockOrientation(Orientation.Portrait);
          processNextHandler();
        } else {
          this._location.back();
        }
      }, 300);
    });
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {

        // First successful route render = the app is demonstrably alive. Mark the
        // launch monitor as responded/complete so a slow-but-healthy load (or a user
        // tapping during load) can no longer trigger a false "Page hiccup" modal.
        if (!this.hasRenderedRoute) {
          this.hasRenderedRoute = true;
          this.performanceLaunchMonitor.markResponseDetected();
          this.performanceLaunchMonitor.markBootstrapComplete();

          // Fetch the static toast messages only AFTER the first route has resolved.
          // getConstantsMsg$ is a shareReplay(refCount) stream, so its network call
          // fires on the first subscription. Subscribing here (instead of at bootstrap)
          // keeps this non-critical call from competing with - and running before - the
          // route's own critical cloud calls (e.g. partialregv2 on partial-registration).
          this.authService?.getConstantsMsg$?.pipe(takeUntil(this.unsubscribe$)).subscribe((msg: any) => {
            this.translatedToastMsg = msg?.static_messages_pwa;
          });
        }

        // Tear down the index.html instant loader once any non-registration route has
        // rendered. This covers every initial hard load (deep links, refresh, resolver
        // redirects to payment/4x pages). The partial-registration flow is the only
        // exception: its component dispatches this itself once the form is ready, so the
        // loader stays up across the resolver's cloud calls instead of flashing away early.
        const finalUrl = (event.urlAfterRedirects || '').toLowerCase();
        if (finalUrl.indexOf('partial-registration') === -1 && finalUrl.indexOf('interim') === -1) {
          try {
            window.dispatchEvent(new CustomEvent('ls:partial-reg-ready'));
          } catch {
            // Non-fatal: index.html has a safety timeout.
          }
        }

        // Remove old classes beginning with "page-"
        document.body.classList.forEach(cls => {
          if (cls.startsWith('page-')) {
            document.body.classList.remove(cls);
          }
        });

        // Convert pathname to safe CSS class
        const cleanPath = event.urlAfterRedirects
          .split('?')[0]            // remove query params
          .replace(/^\/+/, '')      // remove leading slash
          .replace(/\//g, '-')      // convert "/" to "-"
          || 'home';                // default if empty

        const newClass = `page-${cleanPath}`;

        document.body.classList.add(newClass);

        this.handleTipQueryParam(event.urlAfterRedirects || event.url);
      });
  }

  /**
   * Processes `?tip=` deep links. Runs from the constructor NavigationEnd subscription
   * so the initial route is handled before ngOnInit awaits getUid().
   */
  private handleTipQueryParam(url: string): void {
    try {
      const urlObj = new URL(url, window.location.origin);
      const tip = urlObj.searchParams.get("tip");
      if (!tip) {
        return;
      }

      this.narratortipService.narratorTipEvent(tip);
      urlObj.searchParams.delete("tip");
      const query = urlObj.searchParams.toString();
      const newUrl =
        (query.length > 0 ? `${urlObj.pathname}?${query}` : urlObj.pathname) + urlObj.hash;

      this.router.navigateByUrl(newUrl, { replaceUrl: true }).catch((error) => {
        this.authService.errorHandlerLogs("[AppComponent] handleTipQueryParam navigation error", error);
      });
    } catch (error) {
      this.authService.errorHandlerLogs("[AppComponent] handleTipQueryParam error", error);
    }
  }

  async fingerprintJS() {
    try {
      const fpPromise = FingerprintJS.load();
      const fp = await fpPromise;
      const result = await fp.get();
      CommonVariable.FingerprintJSResult = result;
      return result.visitorId;
    } catch (error) {
      return error;
    }
  }

  async ngOnInit() {
    // Automatically handle SW version updates and unrecoverable cache states.
    // This prevents NG05104 caused by stale HTML/JS bundle mismatches on iOS Safari PWA.
    try {
      this.swUpdate.init();
    } catch {
      // Non-fatal: SW unavailable in SSR/prerender or unsupported browsers.
    }

    this.auth.appUpdateOnLoginTrigger$
      .pipe(filter(v => v === true), take(1))
      .subscribe(() => {
        this.swUpdate.activateAndReload()
      });

    await this.authService.getUid("AppComponent").then((uid) => {
      this.userId = uid;
    })
    if (this.userId !== "null" && this.userId !== null) this.sentryContextService.setUserContext(this.userId);
    if (this.userId && this.userId !== "null") {
      this.clarityService.identify(this.userId);
      this.clarityService.setGa4UserId(this.userId);
    }
    this.cleanupAppCheckStorage();
    this.checkLocalStorageAndTokens();
    this.appRouteService.popStateEvent();
    this.authService
      ?.checkIpAnGetUserAddress()
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();
    this.letsLoadAnotherAppThings();
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd | NavigationStart =>
            event instanceof NavigationEnd || event instanceof NavigationStart
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event: Data) => {
        try {
          if (
            event?.url &&
            event?.url?.substring(event?.url?.indexOf("=") + 1, event?.url?.indexOf("&")) &&
            event?.url?.substring(event?.url?.lastIndexOf("=") + 1) &&
            event?.url?.indexOf("psId=") >= 0 &&
            event?.url?.indexOf("memberId") >= 0
          ) {
            if (!this.marketingCampaignsService?.getInvitationData()) {
              this.userVersionService?.setInvitationData(
                event?.url?.substring(event?.url?.indexOf("=") + 1, event?.url?.indexOf("&")),
                event?.url?.substring(event?.url?.lastIndexOf("=") + 1)
              );
            }
          }

          if (event.url && event?.url?.indexOf("custRefId=") >= 0 && event?.url?.indexOf("ref=") >= 0) {
            this.userVersionService?.setReferralData(
              event?.url?.substring(event?.url?.indexOf("custrefID=") + 10, event?.url?.indexOf("&")),
              event?.url?.substring(event?.url?.indexOf("ref=") + 4)
            );
          }
          if (event instanceof NavigationStart) {
            this.appRouteService.updateBasedOnEvent(event);
            const url: string = event?.url || '';
            const hasPaymentDeepLink = url.indexOf('payment=') >= 0 || url.indexOf('hofferid=') >= 0;
            if (!hasPaymentDeepLink) {
              // Allow a future deep-link once payment= is gone from the URL.
              this.lastPaymentDeepLinkKey = null;
              this.appPaymentService.clearPaymentDeepLinkState();
            } else if (this.userId) {
              const paymentMatch = url.match(/[?&]payment=([^&]+)/);
              const hofferMatch = url.match(/[?&]hofferid=([^&]+)/);
              const deepLinkKey = paymentMatch
                ? `payment:${decodeURIComponent(paymentMatch[1])}`
                : hofferMatch
                  ? `hofferid:${decodeURIComponent(hofferMatch[1])}`
                  : url;
              // Prevent repeated NavigationStart retries for the same payment= URL.
              if (this.lastPaymentDeepLinkKey === deepLinkKey) {
                return;
              }
              this.lastPaymentDeepLinkKey = deepLinkKey;
              this.loadingService.present();
              this.route.queryParams
                .pipe(
                  filter((params) => params?.payment || params?.hofferid),
                  take(1),
                  takeUntil(this.unsubscribe$)
                )
                ?.subscribe((params) => {
                  this.appPaymentService.initiatePayment(params);
                });
            }
          }
        } catch (e) {
          return e;
        }
      });
    // Subscribe to performance monitor for initial stuck detection
    this.performanceLaunchMonitor.getStuckDetected()
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(isStuck => {
        if (isStuck) {
          this.checkIfStuckAndShowDialog();
        }
      });

    // Continue with existing interval check for ongoing stuck detection
    interval(5000)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(async () => {
        this.checkIfStuckAndShowDialog();
      });

    // Mark bootstrap complete when Angular is initialized
    setTimeout(() => {
      this.performanceLaunchMonitor.markBootstrapComplete();
    }, 0);
    // this.appInitializerService.errorHandler();
    this.animationService.getAnimationFB(this.userId).pipe(takeUntil(this.unsubscribe$)).subscribe();

    // Capture campaign_id from URL if present
    this.campaignTrackingService.captureCampaignId();

    // Canonical PWA launch / install attribution through Segment
    // (distinguishes installed-app launch, true install, and browser visit).
    this.pwaLaunchTrackingService.init();
  }

  private cleanupAppCheckStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Remove all App Check related session storage items
    const appCheckKeys = [
      'needsAppCheckToken',
      'appCheckErrorShown',
      'appCheckErrorCount',
      'visitedSalesInterim'
    ];

    appCheckKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });

  }

  // method to check localStorage availability and recover missing tokens
  private checkLocalStorageAndTokens() {
    try {
      // Test if localStorage is available
      localStorage.setItem('_test', 'test');
      localStorage.removeItem('_test');

      // Check if token is missing but user ID exists
      const token = localStorage.getItem('fbToken');
      if (this.userId && !token) {
        console.warn('Token missing, attempting to recover');
        this.authService.getCustomToken(this.userId).subscribe(
          (res) => {
            if (res?.customToken) {
              localStorage.setItem('fbToken', res.customToken);
              console.info('Token recovered successfully');
            }
          },
          (err) => console.error('Failed to recover token:', err)
        );
      }
    } catch (e) {
      console.warn('localStorage is not available:', e);
      // Handle case where localStorage is not available
      const msg = {
        message: {
          line_1: "Your web browser does not support storing settings locally. In Safari, the most common cause of this is using 'Private Browsing Mode'. Some settings may not save or some features may not work properly for you.",
          line_2: "",
        },
        style: {
          icon: "tick circle alert.svg",
          type: "warning",
        },
      };

      // Show warning message
      this.authService.showToastMessage(null, msg);
    }
  }

  private letsLoadAnotherAppThings() {
    this.appInitializerService.checkUpdate();
    this.appInitializerService.watchOfflineMode();
    this.appInitializerService.handleRouteEvents();
    this.appInitializerService.getUDID();
  }

  /* Detecting the screen orientation */
  orientationChange() {
    setTimeout(() => {
      if (location.href.search("gamePlay") < 0) {
        if (this.getOrientation() === "LANDSCAPE") {
          this.isLandscapeMode = true;
        } else {
          this.isLandscapeMode = false;
        }
      }
    }, 200);
  }

  getOrientation() {
    try {
      // if (!this.screenOrientation) return;
      // if (CommonVariable.device?.operatingSystem == "android") {
      //   return "POTRAIT";
      // }
      const type = window.screen?.orientation?.type ?? "";
      return type.startsWith("landscape") ? "LANDSCAPE" : "PORTRAIT";
    }
    catch {
      return "PORTRAIT";
    }
  }
  private async getDeviceInfo() {
    if (!Device) {
      return;
    }
    CommonVariable.device = await Device.getInfo();
  }

  /**
   * On first iOS standalone launch, record the fingerprint on add_home_screen
   * so reporting can track install. Ranking retirement uses last_login_display_mode.
   */
  private async markAddHomeScreenInstalledIfNeeded(): Promise<void> {
    try {
      if (
        resolveOperatingSystem(CommonVariable?.device?.operatingSystem) !== "ios" ||
        !UtilityService?.isAppInstalledOnHomePage()
      ) {
        return;
      }

      const userId = await this.authService.getUid("AppComponent.markAddHomeScreenInstalledIfNeeded");
      const fingerprintId = localStorage.getItem("fingerSalt");
      if (!userId || !fingerprintId) {
        return;
      }

      // Fixed add_home_screen id — only report install when CF has already issued the doc.
      const addHomeScreenRef = doc(
        this.firestore,
        `users/${userId}/inline_notification/${ADD_HOME_SCREEN_DOC_ID}`
      );
      const addHomeScreenSnap = await getDoc(addHomeScreenRef);
      if (!addHomeScreenSnap.exists()) {
        return;
      }

      const rawDevices = addHomeScreenSnap.data()?.installed_devices;
      const existingDevices = Array.isArray(rawDevices) ? rawDevices : [];
      const alreadyInstalled = existingDevices.some(
        (device: Data) => device?.fingerprintId === fingerprintId && device?.installed
      );
      if (alreadyInstalled) {
        return;
      }

      const updatedDevices = upsertInstalledDevice(existingDevices, fingerprintId);

      try {
        await firstValueFrom(
          this.authService
            .getV2CloudFunction("updatefirestoredoc", {
              path_id: "AhScRn9k2mPqWx7vB4eT",
              expected_user_id: userId,
              doc_data: {
                installed_devices: updatedDevices,
              },
              method: "update",
            })
            .pipe(take(1))
        );
      } catch {
        // non-blocking reporting write
      }
    } catch {
      // non-blocking
    }
  }

  private async HotJarIdentify() {
    if (this.userId !== "null" || this.userId !== null) {
      this.userAuthDetails$?.pipe(takeUntil(this.unsubscribe$)).subscribe((user: any) => {
        this.GTMSagment.HotJarIdentify(user, CommonVariable.userIP);
      });
    }
  }
  // User Interaction Enable
  isUserInteractionEnabled() {
    // Check if the website is still responsive to user interaction
    // by simulating a click on a hidden button and measuring the time it takes
    const start = performance.now();
    const button = document.createElement("button");
    button.style.display = "none";
    document.body.appendChild(button);
    if (CommonVariable.flagsDropdownOpened === false) {
      button.click();
    }
    const end = performance?.now();
    const elapsed = end - start;
    document.body.removeChild(button);
    return elapsed < this.threshold;
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private async checkIfStuckAndShowDialog() {
    if (this.isStuck || this.checkingStuck || this.destroyed || document.hidden) return;
    this.checkingStuck = true;
    try {
      const user = await firstValueFrom(this.authService.getCurrentUser().pipe(
        timeout(2000), takeUntil(this.unsubscribe$)
      ), { defaultValue: null });
      if (this.isStuck || this.destroyed || document.hidden) return;

      const isBootstrapStuck = this.performanceLaunchMonitor.isBootstrapStuck();
      if (
        (this.interactionService.isAppStuck() &&
          this.healthCheckService.getHealthCheckFailedStatus() &&
          this.performanceService.getLongTaskDetected() &&
          user) ||
        // A rendered route is evidence that a slow bootstrap is not stuck.
        (isBootstrapStuck && navigator.onLine && user && !this.hasRenderedRoute)
      ) {
        this.isStuck = true;
        const dialogRef = this.matDialog.open(StuckWebsiteComponent, {
          panelClass: "app-update-dialog-size",
          backdropClass: "decline-back-drop-color",
          disableClose: true,
        });

        dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$)).subscribe(result => {
          if (result === 'wait') {
            this.isStuck = false;
          }
        });
      }
    } catch {
      // Auth readiness alone is not evidence that the rendered app is stuck.
    } finally {
      this.checkingStuck = false;
    }
  }
}
